<template>
    <b-modal 
        :visible="show" 
        size="xl"
        scrollable 
        no-close-on-backdrop
        no-close-on-esc
        dialog-class="custom-tall-modal"
        centered>
        <template #modal-header="{ close }">
            <div class="w-100 d-flex justify-content-between align-items-center">
                <h5 class="m-0 font-weight-bold">{{ Title }}</h5>
                <b-icon-x-lg @click="show = false" role="button" font-scale="1.2" v-b-tooltip.hover :title="$t('Close')" class="text-muted" />
            </div>
        </template>
        <b-container fluid class="p-0">
            <b-tabs :active-nav-item-class="'font-weight-bold text-' + Variant">
                <b-tab 
                    v-for="(faq, indexF) in Faqs" 
                    :key="indexF" 
                    :title="faq.title"
                    title-link-class="w-100 text-left text-muted faq-tab-hover"
                    :active-nav-item-class="'font-weight-bold bg-' + Variant + ' text-white'">
                    <div>
                        <b-tabs 
                            vertical 
                            nav-wrapper-class="col-12 col-md-3 p-0 border-right" 
                            :active-nav-item-class="'text-' + Variant + ' font-weight-bold bg-' + Variant + '-light custom-thick-border'"
                            content-class="col-12 col-md-9 p-4">
                            <b-tab 
                                v-for="(theme, indexT) in faq.themeFaq" 
                                :key="indexT"
                                :title="theme.title"
                                title-link-class="w-100 text-left border-0 rounded-0 px-4 py-3 text-muted faq-tab-hover"
                                :active-nav-item-class="'font-weight-bold border-0 bg-' + Variant + ' text-white'"> 
                                <div>
                                    <div 
                                        v-for="(qa, indexQa) in theme.qa" 
                                        :key="indexQa" 
                                        class="py-3"
                                        :class="{'border-bottom': indexQa !== theme.qa.length - 1}">
                                        <div 
                                            class="d-flex justify-content-between align-items-center" 
                                            role="button"
                                            @click="handleScroll($event, qa, theme.qa)">
                                            <div class="d-flex align-items-center">
                                                <div 
                                                    class="rounded-circle d-flex align-items-center justify-content-center font-weight-bold border mr-3" 
                                                    style="width: 28px; height: 28px; min-width: 28px; font-size: 0.9rem; transition: all 0.3s;"
                                                    :class="!qa.collapsed ? 'bg-' + Variant + ' text-white border-0' : 'text-dark border-dark'">
                                                    {{ indexQa + 1 }}
                                                </div>
                                                <h6 class="m-0 font-weight-bold" :class="!qa.collapsed ? 'text-' + Variant : 'text-dark'">
                                                    {{ qa.question }}
                                                </h6>
                                            </div>
                                            <b-icon 
                                                :icon="qa.collapsed ? 'plus' : 'dash'" 
                                                font-scale="1.5" 
                                                :class="!qa.collapsed ? 'text-' + Variant : 'text-muted'">
                                            </b-icon>
                                        </div>
                                        <b-collapse :visible="!qa.collapsed" class="mt-3">
                                            <div class="text-muted pl-5" style="line-height: 1.6;" v-html="qa.answer"></div>
                                        </b-collapse>
                                    </div>
                                </div>
                            </b-tab>
                        </b-tabs>
                    </div>
                </b-tab>
            </b-tabs>
        </b-container>
        <template #modal-footer>
            <div class="w-100">
                <b-button
                    @click="show=false"
                    :variant="Variant"
                    size="md"
                    pill
                    class="float-right text-white">
                    {{ $t('Close') }}
                </b-button>
            </div>
        </template>
    </b-modal>
</template>

<script lang="ts">
    import { Component, Prop, PropSync, Vue, Watch } from 'vue-property-decorator';
    import { FAQ } from '@/types/Commons/FAQ';

    @Component({
        components: {}
    })
    export default class FAQComponent extends Vue {

        @Prop()
        private Title!: string;

        @Prop()
        private Variant!: string;
        
        @PropSync("Show", { default : () => false })
        private show!: boolean;

        @Prop()
        private Faqs!: Array<FAQ>;

        private handleScroll(event: Event, question: any, qaList: any[]) {
            const wasCollapsed = question.collapsed;

            qaList.forEach(q => {
                q.collapsed = true;
            });

            if (wasCollapsed) {
                question.collapsed = false;

                const element = event.currentTarget as HTMLElement;
                setTimeout(() => {
                    element.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center'
                    });
                }, 200);
            }
        }

        @Watch('Faqs')
        private async loadLocalJson() {
            for (const faq of this.Faqs) 
            {
                try {
                    const module = await import(`./FAQs/${faq.path}.json`);
                    const data = module.default;
                    faq.themeFaq = data.map((faqGroup: any) => {
                        return {
                            title: faqGroup.title,
                            qa: faqGroup.questionAnswer.map((item: any) => ({
                                question: item.question,
                                answer: item.answer.replace(/\n/g, '<br/>'),
                                collapsed: true
                            }))
                        };
                    });
                } catch (error) {
                    console.error(`Erreur lors du chargement de ${faq.path}.json`, error);
                }
            }
        }

        async created() {
            await this.loadLocalJson();
        }
    }
</script>

<style lang="css" scoped>
/* Hover sur les onglets inactifs */
::v-deep .faq-tab-hover {
    transition: background-color 0.2s, color 0.2s;
}
::v-deep .faq-tab-hover:hover:not(.active) {
    background-color: #f8f9fa;
    color: #333 !important;
}

/* On force l'épaisseur de la bordure gauche pour l'onglet principal actif */
::v-deep .custom-thick-border {
    border-left: 5px solid currentColor !important;
}

/* Gestion de la hauteur de la modale */
::v-deep .custom-tall-modal {
    height: calc(100vh - 4rem) !important;
    max-height: calc(100vh - 4rem) !important;
}

::v-deep .custom-tall-modal .modal-content {
    height: 100% !important;
}
</style>